import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getGroupDiscussionPageUrl, getThreadDetailPageUrl } from 'constants/pages';
import {
  CommentVoteEnum,
  GetGroupRecentCommentsQuery,
  GroupCommentVotes,
  useGetGroupRecentCommentsAnonymousLazyQuery,
  useGetGroupRecentCommentsLazyQuery,
  useGetUserGroupMembershipLazyQuery,
  useUpsertGroupThreadVoteMutation,
  useUpsertUsersGroupMembershipMutation,
} from 'types/generated/client';
import { GetGroupPageContentByGroupSlugQuery } from 'types/generated/server';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { pluralize } from 'utils/shared/pluralize';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useAuthModals } from 'hooks/useAuthModals';
import { useViewer } from 'hooks/useViewer';
import ChatBubble from 'svg/ChatBubble';
import ThumbUp from 'svg/ThumbUp';
import { ButtonText, Button } from 'components/Button';
import Comment from 'components/Comment';
import NoDataPlaceholder from 'components/DiscoverPage/NoDataPlaceholder';
import EmptyAction from 'components/EmptyAction';
import classNames from 'styles/utils/classNames';

const THREADS_TO_SHOW = 3;
const ORIGINAL_COMMENT = 1;

interface RecentCommentsSectionProps {
  groupId: string;
  groupSlug: string;
  group: GetGroupPageContentByGroupSlugQuery['groups'][0];
  fetchData?: () => void;
  openJoinModal?: () => void;
  openSignupModal: (_open?: boolean) => void;
}

const RecentCommentsSection: React.FC<RecentCommentsSectionProps> = ({
  groupId,
  groupSlug,
  group,
  fetchData = () => { },
  openJoinModal,
  openSignupModal,
}) => {
  const router = useRouter();
  const { userId, isAnonymousSession } = useViewer();
  const [isMember, setIsMember] = useState(false);

  const [
    getGroupRecentCommentsLoggedInLazyQuery,
    { data: loggedInData, loading: loggedInLoading, refetch: refetchLoggedIn },
  ] = useGetGroupRecentCommentsLazyQuery();

  const [
    getGroupRecentCommentsAnonymousLazyQuery,
    { data: anonymousData, loading: anonymousLoading, refetch: refetchAnonymous },
  ] = useGetGroupRecentCommentsAnonymousLazyQuery();

  const [upsertGroupThreadVoteMutation] = useUpsertGroupThreadVoteMutation();
  const [upsertUsersGroupMembershipMutation] = useUpsertUsersGroupMembershipMutation();
  const [getUserGroupMembershipLazyQuery, { data: userMembershipData }] =
    useGetUserGroupMembershipLazyQuery();

  const data = userId ? loggedInData : anonymousData;
  const loading = userId ? loggedInLoading : anonymousLoading;

  const isPrivate = group?.isPrivate;

  const refetch = () => {
    if (userId) {
      refetchLoggedIn({
        groupId,
        limit: THREADS_TO_SHOW,
        userId,
      });
    } else {
      refetchAnonymous({
        groupId,
        limit: THREADS_TO_SHOW,
      });
    }
  };

  useEffect(() => {
    if (userId && groupId) {
      getUserGroupMembershipLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId,
          groupId,
        },
      });
    }
  }, [groupId, userId, getUserGroupMembershipLazyQuery]);

  useEffect(() => {
    if (userMembershipData?.groupMembers?.length && userMembershipData?.groupMembers[0]?.isActive) {
      setIsMember(true);
    } else {
      setIsMember(false);
    }
  }, [userMembershipData]);

  useEffect(() => {
    if (groupId && (!isPrivate || (isPrivate && isMember))) {
      if (userId) {
        getGroupRecentCommentsLoggedInLazyQuery({
          variables: {
            groupId,
            limit: THREADS_TO_SHOW,
            userId,
          },
        });
      } else {
        getGroupRecentCommentsAnonymousLazyQuery({
          variables: {
            groupId,
            limit: THREADS_TO_SHOW,
          },
        });
      }
    }
  }, [groupId, isPrivate, isMember, userId]);
  const threads = data?.groupThreads || [];
  const hasThreads = threads.length > 0;

  if (isPrivate && !isMember) {
    return null;
  }

  const handleViewAllComments = () => {
    router.push(getGroupDiscussionPageUrl(groupSlug));
  };

  const handleJoinGroup = async () => {
    if (isAnonymousSession) {
      openSignupModal();
      return;
    }

    try {
      await upsertUsersGroupMembershipMutation({
        variables: {
          userId,
          groupId,
          isActive: true,
        },
        optimisticResponse: {
          __typename: 'mutation_root',
          insertGroupMembersOne: {
            __typename: 'GroupMembers',
            userId,
            groupId,
            isActive: true,
          },
        },
      });

      fetchData();
      getUserGroupMembershipLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          userId,
          groupId,
        },
      });
    } catch (error) {
      console.error('Error joining group:', error);
    }
  };

  const handleAuthRequired = async (callback: () => void): Promise<boolean> => {
    if (isAnonymousSession) {
      openSignupModal();
      return false;
    }

    if (!isMember) {
      if (!isPrivate) {
        await handleJoinGroup();
        callback();
      } else {
        console.log('im here');
        openJoinModal?.();
      }
      return false;
    }

    return true;
  };

  const handleUpvote = async (
    commentId: string,
    currentVote: GroupCommentVotes | null | undefined,
  ) => {
    const proceed = await handleAuthRequired(() => handleUpvoteAction(commentId, currentVote));
    if (proceed) {
      handleUpvoteAction(commentId, currentVote);
    }
  };

  const handleUpvoteAction = async (
    commentId: string,
    userVote: GroupCommentVotes | null | undefined,
  ) => {
    if (!userId) return;

    const isUserUpvoted = userVote?.vote === CommentVoteEnum.Positive;

    const variables = {
      groupThreadCommentId: commentId,
      userId: userId,
      vote: isUserUpvoted ? CommentVoteEnum.None : CommentVoteEnum.Positive,
    };

    await upsertGroupThreadVoteMutation({
      variables,
      optimisticResponse: {
        __typename: 'mutation_root',
        insertGroupCommentVotesOne: {
          __typename: 'GroupCommentVotes',
          ...variables,
          id: userVote?.id || '',
        },
      },
    });

    refetch();
  };

  const handleReply = async (threadId: string) => {
    const proceed = await handleAuthRequired(() =>
      router.push(getThreadDetailPageUrl(groupSlug, threadId)),
    );

    if (proceed) {
      router.push(getThreadDetailPageUrl(groupSlug, threadId));
    }
  };

  const handleCommentClick = (threadId: string) => {
    router.push(getThreadDetailPageUrl(groupSlug, threadId));
  };

  if (loading) {
    return (
      <div className="mx-auto mt-ds-2xl max-w-7xl">
        <div className="relative mb-4 flex items-center justify-between leading-none">
          <h2 className="typography-product-heading">Recent discussions</h2>
        </div>
        <div className="border-b border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
          <div className="flex flex-col gap-4">
            {[...Array(2)].map((_, index) => (
              <div
                key={index}
                className="w-full animate-pulse rounded-lg bg-color-bg-lightmode-secondary p-4 dark:bg-color-bg-darkmode-secondary"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                  <div className="flex-1">
                    <div className="mb-2 h-4 w-1/4 rounded bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                    <div className="h-16 w-full rounded bg-color-bg-lightmode-tertiary dark:bg-color-bg-darkmode-tertiary"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-ds-2xl max-w-7xl">
      <div className="relative mb-4 flex items-center justify-between leading-none">
        <h2 className="typography-product-heading">Recent discussions</h2>
        {hasThreads && (
          <ButtonText
            onClick={handleViewAllComments}
            size="sm"
            className="text-color-text-lightmode-brand dark:text-color-text-darkmode-brand"
          >
            View all
          </ButtonText>
        )}
      </div>
      <div className="border-b border-color-border-input-lightmode pb-10 dark:border-color-border-input-darkmode">
        {hasThreads ? (
          <div className="flex flex-col gap-8 divide-y divide-color-border-input-lightmode dark:divide-color-border-input-darkmode">
            {threads.map((thread) => {
              const comment = thread.comments.find((comment) => comment.isOriginalThreadComment);
              if (!comment) return null;

              const userVote = (
                comment as GetGroupRecentCommentsQuery['groupThreads'][0]['comments'][0]
              )?.votes?.[0];
              const isUserUpvoted = userVote?.vote === CommentVoteEnum.Positive;

              return (
                <div key={thread.id} className="flex flex-col pt-8 first:pt-0">
                  <Comment
                    id={comment.id}
                    type="group"
                    content={comment.content}
                    senderName={comment?.userProfile?.fullName}
                    senderUsername={comment?.userProfile?.username}
                    senderProfileImageUrl={getProfileImageUrlOrPlaceholder({
                      path: comment?.userProfile?.profileImagePath,
                    })}
                    timestamp={comment.createdAt}
                    files={comment.files?.map((file) =>
                      getImageUrl({
                        url: file.url,
                        path: file.path,
                      }),
                    )}
                  />
                  <div className="mt-4 flex items-center gap-6 pt-2 text-sm font-medium leading-4 dark:border-color-border-input-darkmode">
                    <Button
                      variant="brand"
                      isInline
                      size="sm"
                      onClick={() => handleReply(thread.id)}
                      className="flex items-center py-2"
                      iconLeft={<ChatBubble className="mr-1 h-4 w-4" />}
                    >
                      Reply
                    </Button>
                    <ButtonText className="py-2" onClick={() => handleCommentClick(thread.id)}>
                      {pluralize({
                        count: Math.max(
                          (thread?.commentsAggregate?.aggregate?.count || 0) - ORIGINAL_COMMENT,
                          0,
                        ),
                        singular: 'Comment',
                        plural: 'Comments',
                      })}
                    </ButtonText>
                    <ButtonText
                      onClick={() => handleUpvote(comment.id, userVote as GroupCommentVotes)}
                      className={classNames(
                        'flex items-center rounded-2xl px-2 py-1',
                        isUserUpvoted && 'bg-brand-fire-50 text-brand-fire-500',
                      )}
                    >
                      <ThumbUp className="mr-1 h-4 w-4" />{' '}
                      {comment?.votesAggregate?.aggregate?.count
                        ? comment?.votesAggregate?.aggregate?.count
                        : ''}
                    </ButtonText>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyAction
            description="No Comments yet"
            actionUrl={isPrivate ? null : getGroupDiscussionPageUrl(groupSlug)}
            actionText={isPrivate ? null : 'Add a post'}
          />
        )}
      </div>
    </div>
  );
};

export default RecentCommentsSection;
